import { useEffect, useMemo, useState } from "react";
import {
  ExternalLink,
  Gavel,
  LoaderCircle,
  MapPin,
  Search,
  Trash2,
  UserPlus,
  UsersRound,
} from "lucide-react";
import { CopyableText, FeedbackBanner, PageHeader } from "@tribunalea/ui";
import { useApi } from "../App.jsx";

export function LawyersPage() {
  const api = useApi();
  const [lawyers, setLawyers] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    api
      .listPraxisLawyers()
      .then((items) => {
        if (active) setLawyers(items);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoadingRoster(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const registrationKeys = useMemo(
    () =>
      new Set(
        lawyers.map((lawyer) =>
          String(lawyer.registration?.number || "").toLocaleUpperCase(
            "es-AR",
          ),
        ),
      ),
    [lawyers],
  );

  const search = async (event) => {
    event.preventDefault();
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 2 || searching) return;
    setSearching(true);
    setSearched(false);
    setError("");
    try {
      const data = await api.searchProfessionals(normalizedQuery);
      setResults(
        (data.items || []).filter(
          (professional) =>
            professional.profession === "lawyer_or_solicitor",
        ),
      );
      setSearched(true);
    } catch (requestError) {
      setResults([]);
      setSearched(true);
      setError(requestError.message);
    } finally {
      setSearching(false);
    }
  };

  const addLawyer = async (professional) => {
    const registration = professional.registrations?.[0];
    if (!registration?.number || savingId) return;
    setSavingId(professional.id);
    setError("");
    try {
      const lawyer = await api.addPraxisLawyer({
        directoryEntryId: professional.id,
        registrationNumber: registration.number,
      });
      setLawyers((current) =>
        [...current.filter((item) => item.id !== lawyer.id), lawyer].sort(
          (left, right) =>
            left.fullName.localeCompare(right.fullName, "es-AR"),
        ),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingId("");
    }
  };

  const removeLawyer = async (lawyer) => {
    if (removingId) return;
    setRemovingId(lawyer.id);
    setError("");
    try {
      await api.removePraxisLawyer(lawyer.id);
      setLawyers((current) =>
        current.filter((item) => item.id !== lawyer.id),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setRemovingId("");
    }
  };

  return (
    <div className="page lawyers-page">
      <PageHeader
        eyebrow="Administración"
        title="Nómina de abogados"
        description="Registrá a los abogados que trabajan en este espacio, aunque no tengan un usuario en Tribunalea."
      />

      {error && <FeedbackBanner>{error}</FeedbackBanner>}

      <section className="lawyer-directory-card">
        <div className="lawyer-section-heading">
          <span>
            <Search size={19} />
          </span>
          <div>
            <h2>Agregar desde el padrón</h2>
            <p>
              Buscá por nombre, apellido o matrícula. Praxis completará los
              datos disponibles.
            </p>
          </div>
        </div>

        <form className="lawyer-search-form" onSubmit={search}>
          <label htmlFor="lawyer-directory-query">
            Abogado o matrícula
            <span>
              <Search size={18} />
              <input
                id="lawyer-directory-query"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ej. Apellido o 0000-A"
                autoComplete="off"
              />
            </span>
          </label>
          <button
            type="submit"
            className="button primary"
            disabled={query.trim().length < 2 || searching}
          >
            {searching ? (
              <LoaderCircle className="spin" size={17} />
            ) : (
              <Search size={17} />
            )}
            {searching ? "Buscando…" : "Buscar"}
          </button>
        </form>

        {results.length > 0 && (
          <div className="lawyer-search-results">
            {results.map((professional) => {
              const registration = professional.registrations?.[0];
              const alreadyAdded = registrationKeys.has(
                String(registration?.number || "").toLocaleUpperCase(
                  "es-AR",
                ),
              );
              return (
                <article key={professional.id}>
                  <span className="lawyer-avatar">
                    <Gavel size={20} />
                  </span>
                  <div className="lawyer-result-copy">
                    <h3>{professional.fullName}</h3>
                    <p>
                      <strong>
                        {registration?.number ? (
                          <CopyableText
                            copiedMessage="Matrícula copiada"
                            label="matrícula"
                            value={registration.number}
                          >
                            Matrícula {registration.number}
                          </CopyableText>
                        ) : "Matrícula no informada"}
                      </strong>
                      <span>
                        <MapPin size={13} />
                        {professional.source?.jurisdictionLabel ||
                          "Jurisdicción no informada"}
                      </span>
                    </p>
                    <small>
                      Fuente:{" "}
                      {professional.source?.label || "Padrón profesional"}
                    </small>
                  </div>
                  <button
                    type="button"
                    className={alreadyAdded ? "button muted" : "button secondary"}
                    disabled={
                      alreadyAdded || Boolean(savingId) || !registration?.number
                    }
                    onClick={() => addLawyer(professional)}
                  >
                    {savingId === professional.id ? (
                      <LoaderCircle className="spin" size={16} />
                    ) : (
                      <UserPlus size={16} />
                    )}
                    {alreadyAdded ? "En la nómina" : "Agregar"}
                  </button>
                </article>
              );
            })}
          </div>
        )}

        {searched && !results.length && !error && (
          <div className="lawyer-search-empty">
            <Search size={20} />
            <span>
              No encontramos coincidencias. Probá con el apellido o la
              matrícula sin espacios.
            </span>
          </div>
        )}
      </section>

      <section className="lawyer-roster-card">
        <div className="lawyer-roster-header">
          <div>
            <span className="eyebrow">Espacio activo</span>
            <h2>Abogados registrados</h2>
          </div>
          <strong>
            {lawyers.length} {lawyers.length === 1 ? "abogado" : "abogados"}
          </strong>
        </div>

        {loadingRoster ? (
          <div className="lawyer-roster-empty">
            <LoaderCircle className="spin" size={22} />
            <span>Cargando la nómina…</span>
          </div>
        ) : lawyers.length ? (
          <div className="lawyer-roster-list">
            {lawyers.map((lawyer) => (
              <article key={lawyer.id}>
                <span className="lawyer-avatar">
                  <Gavel size={20} />
                </span>
                <div>
                  <h3>{lawyer.fullName}</h3>
                  <p>
                    <CopyableText
                      copiedMessage="Matrícula copiada"
                      label="matrícula"
                      value={lawyer.registration.number}
                    >
                      Matrícula {lawyer.registration.number}
                    </CopyableText>
                    {lawyer.registration.district
                      ? ` · ${lawyer.registration.district}`
                      : ""}
                  </p>
                  <small>
                    {lawyer.source.label}
                    {lawyer.source.url && (
                      <a
                        href={lawyer.source.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ver fuente <ExternalLink size={12} />
                      </a>
                    )}
                  </small>
                </div>
                <button
                  type="button"
                  className="icon-button danger"
                  aria-label={`Quitar a ${lawyer.fullName} de la nómina`}
                  title="Quitar de la nómina"
                  disabled={Boolean(removingId)}
                  onClick={() => removeLawyer(lawyer)}
                >
                  {removingId === lawyer.id ? (
                    <LoaderCircle className="spin" size={17} />
                  ) : (
                    <Trash2 size={17} />
                  )}
                </button>
              </article>
            ))}
          </div>
        ) : (
          <div className="lawyer-roster-empty">
            <UsersRound size={24} />
            <h3>Todavía no agregaste abogados</h3>
            <p>
              Buscá el primer profesional en el padrón para comenzar la
              nómina de este espacio.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
